import { type Edge } from '@xyflow/react'
import { type TChatbotNode } from './node.type'
import { EVariableType } from 'src/types/variable'
import {
  MEDIA_UPLOADED_HANDLE,
  NODE_VERTICAL_SPACING,
  NODE_HORIZONTAL_OFFSET,
  COLLISION_THRESHOLD,
  NODE_SPACING,
} from './chatbot.constants'

/**
 * Calculate position for a new node connected to a parent node (simple connection)
 */
export function calculateNodePosition(
  parentNode: TChatbotNode,
  existingNodes: TChatbotNode[],
): { x: number; y: number } {
  // Calculate initial position
  let targetX = parentNode.position.x
  const targetY = parentNode.position.y + NODE_VERTICAL_SPACING

  // Check for collision with existing nodes
  let hasCollision = true
  let offsetMultiplier = 0

  while (hasCollision && offsetMultiplier < 10) {
    hasCollision = existingNodes.some((node) => {
      const dx = Math.abs(node.position.x - targetX)
      const dy = Math.abs(node.position.y - targetY)
      return dx < COLLISION_THRESHOLD && dy < NODE_VERTICAL_SPACING
    })

    if (hasCollision) {
      offsetMultiplier++
      // Alternate between right and left offset
      const direction = offsetMultiplier % 2 === 0 ? 1 : -1
      const offset = Math.ceil(offsetMultiplier / 2) * NODE_SPACING
      targetX = parentNode.position.x + direction * offset
    }
  }

  return { x: targetX, y: targetY }
}

/**
 * Calculate horizontal offset for a conditional connection based on condition key
 */
function calculateHorizontalOffset(conditionKey: string, parentNode: TChatbotNode): number {
  let horizontalOffset = NODE_HORIZONTAL_OFFSET

  // Check if this is a range condition
  const rangeMatch = /^range-(\d+)$/.exec(conditionKey)
  if (rangeMatch) {
    const rangeIndex = Number.parseInt(rangeMatch[1]!, 10)
    const variable = parentNode.data.variable

    if (variable?.type === EVariableType.INTEGER_VALUE_RANGE) {
      const totalRanges = variable.condition?.ranges.length || 1

      // Distribute ranges evenly across horizontal space
      // For 1 range: 0 (centered)
      // For 2 ranges: -75, 75
      // For 3 ranges: -150, 0, 150
      // For 4 ranges: -225, -75, 75, 225
      const spacing = NODE_HORIZONTAL_OFFSET
      const totalWidth = (totalRanges - 1) * spacing
      const startOffset = -totalWidth / 2
      horizontalOffset = startOffset + rangeIndex * spacing
    }
  } else if (conditionKey === 'yes' || conditionKey === MEDIA_UPLOADED_HANDLE) {
    horizontalOffset = -NODE_HORIZONTAL_OFFSET
  }
  // else: keep default (NODE_HORIZONTAL_OFFSET for 'no')

  return horizontalOffset
}

/**
 * Calculate position for a new node with a conditional connection
 */
export function calculateConditionalNodePosition(
  parentNode: TChatbotNode,
  conditionKey: string,
  existingNodes: TChatbotNode[],
  existingEdges: Edge[],
  parentNodeId: string,
): { x: number; y: number } {
  const horizontalOffset = calculateHorizontalOffset(conditionKey, parentNode)

  // Calculate initial position
  let targetX = parentNode.position.x + horizontalOffset
  const targetY = parentNode.position.y + NODE_VERTICAL_SPACING

  // Get sibling nodes (nodes connected to the same parent)
  const siblingNodeIds = new Set(existingEdges.filter((e) => e.source === parentNodeId).map((e) => e.target))

  // Check for collision with existing nodes (excluding siblings)
  let hasCollision = true
  let offsetMultiplier = 0

  while (hasCollision && offsetMultiplier < 10) {
    hasCollision = existingNodes.some((node) => {
      // Skip collision detection with sibling nodes
      if (siblingNodeIds.has(node.id)) return false

      const dx = Math.abs(node.position.x - targetX)
      const dy = Math.abs(node.position.y - targetY)
      return dx < COLLISION_THRESHOLD && dy < NODE_VERTICAL_SPACING
    })

    if (hasCollision) {
      offsetMultiplier++
      // Alternate between right and left offset
      const direction = offsetMultiplier % 2 === 0 ? 1 : -1
      const offset = Math.ceil(offsetMultiplier / 2) * NODE_SPACING
      targetX = parentNode.position.x + horizontalOffset + direction * offset
    }
  }

  return { x: targetX, y: targetY }
}
